import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
  OnDestroy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { baseUrl } from '../../../../../common/constants/endpoints.constants';
import { CustomizedDropdownComponent } from '../../../../../../ui-components/lib/customized-dropdown/customized-dropdown.component';
import { InputComponent } from '../../../../../../ui-components/lib/input/input.component';
import { OptionItem } from '../../../../../../ui-components/lib/option.interface';
import { PaginatedSelectComponent } from '../../../../../../ui-components/lib/paginated-select/paginated-select.component';
import { RichEditorDualLangTextAreaComponent } from '../../../../../../ui-components/lib/rich-editor-dual-lang-text-area/rich-editor-dual-lang-text-area.component';
import { SelectComponent } from '../../../../../../ui-components/lib/select/select.component';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';

@Component({
  selector: 'lib-reassign-modal',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    CustomizedDropdownComponent,
    InputComponent,
    PaginatedSelectComponent,
    SelectComponent,
    ValidationMessageComponent,
    RichEditorDualLangTextAreaComponent,
  ],
  templateUrl: './reassign-modal.component.html',
  styleUrl: './reassign-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ReassignModalComponent implements OnDestroy {
  translocoService = inject(TranslocoService);
  fb = inject(FormBuilder);
  stepId = input.required<string | number>();
  closeModal = output<void>();
  submitAssignee = output<any>();
  baseUrl = baseUrl;

  assigneeForm = signal<FormGroup>(
    this.fb.group({
      assignable_type: ['', Validators.required],
      assignable_id: ['', Validators.required],
      option: [null],
      preference_option: [null],
      preference: [null, Validators.min(1)],
      reason_option_id: ['', Validators.required],
      comment: ['', [Validators.required]],
    })
  );

  constructor() {
    this.formValueChangesSubscription =
      this.assigneeForm().valueChanges.subscribe(() => {
        this.checkFormValidity();
      });
  }

  // Local state
  selectedAssignableTypeOption = signal<OptionItem | undefined>(undefined);
  selectedAssignableOption = signal<OptionItem | undefined>(undefined);
  selectedAssignmentPreferenceOption = signal<OptionItem | undefined>(
    undefined
  );
  currentSelectedResource = signal<any>(null);
  isFormValid = signal<boolean>(false);
  resourcePreferenceSubscription?: Subscription;
  formValueChangesSubscription?: Subscription;

  // Option arrays
  assigneeTypeOptions: OptionItem[] = [
    {
      label: this.translocoService.translate('request_screen_department_label'),
      value: 'department',
    },
    {
      label: this.translocoService.translate('request_screen_group_label'),
      value: 'group',
    },
    {
      label: this.translocoService.translate(
        'request_screen_legal_entity_label'
      ),
      value: 'legal_entity',
    },
    {
      label: this.translocoService.translate('request_screen_user_label'),
      value: 'user',
    },
  ];

  groupAssignableOptions: OptionItem[] = [
    {
      label: this.translocoService.translate('request_screen_members_label'),
      value: 'members',
    },
  ];

  departmentAssignableOptions: OptionItem[] = [
    {
      label: this.translocoService.translate('request_screen_manager_label'),
      value: 'manager',
    },
    {
      label: this.translocoService.translate('request_screen_members_label'),
      value: 'members',
    },
    {
      label: this.translocoService.translate(
        'request_screen_members_and_sub_department_members_label'
      ),
      value: 'members_and_sub_members',
    },
  ];

  assignmentPreferenceOptions: OptionItem[] = [
    {
      label: this.translocoService.translate(
        'request_screen_all_assignees_label'
      ),
      value: 'all_assignees',
    },
    {
      label: this.translocoService.translate('request_screen_at_least_label'),
      value: 'at_least',
    },
  ];

  assigneeSelectDependency = signal<any>({
    api: '',
    selectedOption: { label: '', value: '' },
    placeholder: this.translocoService.translate(
      'request_screen_select_resource_placeholder'
    ),
    filters: [],
    searchLabel: '',
    searchPlaceholder: '',
  });

  onAssignableTypeChange(event: any): void {
    const type = event.value;
    if (!type || type === this.selectedAssignableTypeOption()?.value) return;

    this.selectAssignableType(event);
    // Enable the assignable_id control when a valid type is selected
    this.assigneeForm().get('assignable_id')?.enable({ emitEvent: false });
  }

  selectAssignableType(event: any): void {
    const type = event.value;
    this.selectedAssignableTypeOption.set(event);

    this.assigneeForm().patchValue(
      {
        assignable_type: type,
        assignable_id: '',
        option: null,
        preference_option: null,
        preference: null,
      },
      { emitEvent: false }
    );

    this.selectedAssignableOption.set(undefined);
    this.selectedAssignmentPreferenceOption.set(undefined);
    this.currentSelectedResource.set(null);

    const assignee = {
      type: type,
      data: { id: '', name: '' },
    };
    this.assigneeSelectDependency.set(this.getAssigneeType(assignee));
    this.updateFormValidators(type);
    this.checkFormValidity();
  }

  selectAssignableOption(event: any): void {
    const option = event.value;
    this.selectedAssignableOption.set(event);
    this.assigneeForm().get('option')?.setValue(option, { emitEvent: false });

    const assignmentPreferenceControl =
      this.assigneeForm().get('preference_option');
    const resourcePreferenceControl = this.assigneeForm().get('preference');
    const resourceType = this.assigneeForm().get('assignable_type')?.value;

    if (option === 'manager') {
      assignmentPreferenceControl?.setValue(null, { emitEvent: false });
      assignmentPreferenceControl?.clearValidators();
      resourcePreferenceControl?.setValue(null, { emitEvent: false });
      resourcePreferenceControl?.clearValidators();
      this.selectedAssignmentPreferenceOption.set(undefined);
    } else {
      if (resourceType === 'group' || resourceType === 'department') {
        assignmentPreferenceControl?.addValidators(Validators.required);
      }
    }

    assignmentPreferenceControl?.updateValueAndValidity({ emitEvent: false });
    resourcePreferenceControl?.updateValueAndValidity({ emitEvent: false });
    this.checkFormValidity();
  }

  selectAssignmentPreference(event: any): void {
    const preference = event.value;
    this.selectedAssignmentPreferenceOption.set(event);
    this.assigneeForm()
      .get('preference_option')
      ?.setValue(preference, { emitEvent: false });

    const resourcePreferenceControl = this.assigneeForm().get('preference');

    if (preference === 'at_least') {
      resourcePreferenceControl?.addValidators([
        Validators.required,
        Validators.min(1),
      ]);
    } else {
      resourcePreferenceControl?.setValue(null, { emitEvent: false });
      resourcePreferenceControl?.clearValidators();
    }

    resourcePreferenceControl?.updateValueAndValidity({ emitEvent: false });
    this.checkFormValidity();
  }

  selectResource(event: any): void {
    this.assigneeForm()
      .get('assignable_id')
      ?.setValue(event.value, { emitEvent: false });
    this.currentSelectedResource.set(event);

    const currentDependency = this.assigneeSelectDependency();
    this.assigneeSelectDependency.set({
      ...currentDependency,
      selectedOption: {
        label: event.label,
        value: event.value,
      },
    });
    this.checkFormValidity();
  }

  getAssignableTypeLabel(): string {
    const assignableType = this.assigneeForm().get('assignable_type')?.value;

    switch (assignableType) {
      case 'user':
        return this.translocoService.translate(
          'request_screen_select_user_label'
        );
      case 'group':
        return this.translocoService.translate(
          'request_screen_select_group_label'
        );
      case 'department':
        return this.translocoService.translate(
          'request_screen_select_department_label'
        );
      case 'legal_entity':
        return this.translocoService.translate(
          'request_screen_select_legal_entity_label'
        );
      default:
        return this.translocoService.translate(
          'request_screen_select_resource_placeholder'
        );
    }
  }

  onSubmit(): void {
    if (this.assigneeForm().valid) {
      const formValue = this.assigneeForm().getRawValue();
      this.submitAssignee.emit(formValue);
    } else {
      this.assigneeForm().markAsDirty();
      this.assigneeForm().markAllAsTouched();
    }
  }

  onClose(): void {
    this.closeModal.emit();
  }

  checkFormValidity(): void {
    const form = this.assigneeForm();
    const assignableType = form.get('assignable_type')?.value;
    const assignableId = form.get('assignable_id')?.value;
    const reasonOptionId = form.get('reason_option_id')?.value;
    const comment = form.get('comment')?.value;
    const option = form.get('option')?.value;
    const preferenceOption = form.get('preference_option')?.value;
    const preference = form.get('preference')?.value;

    let isValid = false;

    if (!assignableType || !assignableId || !reasonOptionId || !comment) {
      isValid = false;
    } else {
      switch (assignableType) {
        case 'user':
          isValid = true;
          break;
        case 'group':
        case 'department':
          if (!option) {
            isValid = false;
          } else if (option === 'manager') {
            isValid = true;
          } else {
            if (!preferenceOption) {
              isValid = false;
            } else if (preferenceOption === 'at_least') {
              isValid = !!(preference && preference > 0);
            } else {
              isValid = true;
            }
          }
          break;
        case 'legal_entity':
          if (!preferenceOption) {
            isValid = false;
          } else if (preferenceOption === 'at_least') {
            isValid = !!(preference && preference > 0);
          } else {
            isValid = true;
          }
          break;
        default:
          isValid = false;
      }
    }

    this.isFormValid.set(isValid);
  }

  private getAssigneeType(assignee: any): any {
    const baseApiUrl = `${baseUrl}/lookup`;
    const defaultFilters = [{ key: 'search', value: '' }];
    const activeStateFilter = [{ key: 'state', value: 'active' }];

    const defaultSelectedOption = {
      label: assignee.data?.name ?? '',
      value: assignee.data?.id ?? '',
    };

    switch (assignee.type) {
      case 'department':
        return {
          api: `${baseApiUrl}/departments`,
          selectedOption: defaultSelectedOption,
          placeholder: this.translocoService.translate(
            'request_screen_select_department_placeholder'
          ),
          filters: [...defaultFilters, ...activeStateFilter],
          searchLabel: this.translocoService.translate(
            'request_screen_department_search_label'
          ),
          searchPlaceholder: this.translocoService.translate(
            'request_screen_search_departments_placeholder'
          ),
        };
      case 'group':
        return {
          api: `${baseApiUrl}/groups`,
          selectedOption: defaultSelectedOption,
          placeholder: this.translocoService.translate(
            'request_screen_select_group_placeholder'
          ),
          filters: [...defaultFilters, ...activeStateFilter],
          searchLabel: this.translocoService.translate(
            'request_screen_group_search_label'
          ),
          searchPlaceholder: this.translocoService.translate(
            'request_screen_search_groups_placeholder'
          ),
        };
      case 'legal_entity':
        return {
          api: `${baseApiUrl}/legal-entities`,
          selectedOption: defaultSelectedOption,
          placeholder: this.translocoService.translate(
            'request_screen_select_legal_entity_placeholder'
          ),
          filters: [...defaultFilters, ...activeStateFilter],
          searchLabel: this.translocoService.translate(
            'request_screen_legal_entity_search_label'
          ),
          searchPlaceholder: this.translocoService.translate(
            'request_screen_search_legal_entities_placeholder'
          ),
        };
      default:
        return {
          api: `${baseApiUrl}/users?filters[not_states][0]=inactive&filters[not_states][1]=cancelled`,
          selectedOption: defaultSelectedOption,
          placeholder: this.translocoService.translate(
            'request_screen_select_user_placeholder'
          ),
          filters: [...defaultFilters],
          searchLabel: this.translocoService.translate(
            'request_screen_user_search_label'
          ),
          searchPlaceholder: this.translocoService.translate(
            'request_screen_search_users_placeholder'
          ),
        };
    }
  }

  private updateFormValidators(type: string, preserveValues = false): void {
    const optionControl = this.assigneeForm().get('option');
    const assignmentPreferenceControl =
      this.assigneeForm().get('preference_option');
    const resourcePreferenceControl = this.assigneeForm().get('preference');

    optionControl?.clearValidators();
    assignmentPreferenceControl?.clearValidators();
    resourcePreferenceControl?.clearValidators();

    if (type === 'user') {
      if (!preserveValues) {
        optionControl?.setValue(null, { emitEvent: false });
        assignmentPreferenceControl?.setValue(null, { emitEvent: false });
        resourcePreferenceControl?.setValue(null, { emitEvent: false });
      }
    } else if (type === 'legal_entity') {
      assignmentPreferenceControl?.addValidators(Validators.required);
      if (!preserveValues) {
        optionControl?.setValue(null, { emitEvent: false });
      }
      if (this.assigneeForm().get('preference_option')?.value === 'at_least') {
        resourcePreferenceControl?.addValidators([
          Validators.required,
          Validators.min(1),
        ]);
      }
    } else if (type === 'group' || type === 'department') {
      optionControl?.addValidators(Validators.required);
      assignmentPreferenceControl?.addValidators(Validators.required);
      if (this.assigneeForm().get('preference_option')?.value === 'at_least') {
        resourcePreferenceControl?.addValidators([
          Validators.required,
          Validators.min(1),
        ]);
      }
    }

    optionControl?.updateValueAndValidity({ emitEvent: false });
    assignmentPreferenceControl?.updateValueAndValidity({ emitEvent: false });
    resourcePreferenceControl?.updateValueAndValidity({ emitEvent: false });
  }

  ngOnDestroy(): void {
    this.resourcePreferenceSubscription?.unsubscribe();
    this.formValueChangesSubscription?.unsubscribe();
  }
}
